<template>
  <v-navigation-drawer
    app
    permanent
    class="app-sidebar"
    :rail="isCollapsed"
    :width="sidebarWidth"
    :rail-width="sidebarRailWidth"
  >
    <div class="sidebar-navigation" :class="{ 'sidebar-navigation--collapsed': isCollapsed }">
      <div class="sidebar-toggle" @click="toggleSidebar" :title="isCollapsed?'Expand':'Collapse'">
        <v-icon size="small">{{ isCollapsed ? 'mdi-chevron-double-right' : 'mdi-chevron-double-left' }}</v-icon>
      </div>

      <div v-if="!isCollapsed" class="sidebar-items">
        <div v-for="item in menuItems" :key="item.title" class="sidebar-section">
          <div
            class="sidebar-standalone"
            :class="{ 'sidebar-standalone--active': isItemActive(item) }"
            @click="navigateTo(item)"
          >
            <div class="sidebar-item-content">
              <v-icon class="sidebar-section-icon">{{ item.icon }}</v-icon>
              <span class="sidebar-section-title">{{ item.title }}</span>
            </div>
          </div>
        </div>
      </div>

      <div v-else class="sidebar-collapsed-items">
        <v-tooltip v-for="item in menuItems" :key="item.title" location="end">
          <template #activator="{ props: tooltipProps }">
            <div
              class="sidebar-collapsed-icon"
              :class="{ 'sidebar-collapsed-icon--active': isItemActive(item) }"
              v-bind="tooltipProps"
              @click="navigateTo(item)"
            >
              <v-icon class="collapsed-icon-i">{{ item.icon }}</v-icon>
            </div>
          </template>
          {{ item.title }}
        </v-tooltip>
      </div>
    </div>
  </v-navigation-drawer>
</template>

<script setup lang="ts">
  import { useRoles } from '@composables/useRoles';
  import { useSidebarState } from '@composables/useSidebarState';
  import { computed } from 'vue';
  import { type RouteLocationRaw, useRoute, useRouter } from 'vue-router';

  type MenuItem = {
    title: string;
    icon: string;
    to: RouteLocationRaw;
  };

  const { isCollapsed, toggleSidebar } = useSidebarState();
  const { isBankUser, isClientSuccessUser, isApproverUser, isAdminUser } = useRoles();
  const route = useRoute();
  const router = useRouter();

  const sidebarWidth = 256;
  const sidebarRailWidth = 72;

  const isNotBankOrClientSuccess = computed(() => {
    return !(isBankUser.value || isClientSuccessUser.value);
  });

  const menuItems = computed<MenuItem[]>(() => {
    const items: MenuItem[] = [];

    if (isNotBankOrClientSuccess.value) {
      items.push(
        {
          title: 'Dashboard',
          icon: 'mdi-chart-box',
          to: { name: 'RulesDashboard', params: { type: 'all' } },
        },
        {
          title: 'Loan Servicing',
          icon: 'mdi-finance',
          to: { name: 'LoanServicingDecision' },
        },
        {
          title: 'Loan Servicing Config',
          icon: 'mdi-code-json',
          to: { name: 'LoanServicingConfig' },
        },
        {
          title: 'Loan Offers',
          icon: 'mdi-handshake-outline',
          to: { name: 'LoanOffersDecision' },
        },
        {
          title: 'Loan Offers Config',
          icon: 'mdi-tune-variant',
          to: { name: 'LoanOffersConfig' },
        }
      );
    }

    if (isApproverUser.value) {
      items.push({
        title: 'Approval',
        icon: 'mdi-book-check',
        to: { name: 'RulesDashboard', params: { type: 'approve' } },
      });
    }

    if (isAdminUser.value) {
      items.push(
        {
          title: 'Users',
          icon: 'mdi-account-cog',
          to: { name: 'UsersDashboard' },
        },
        {
          title: 'Tenants',
          icon: 'mdi-bank-outline',
          to: { name: 'TenantsDashboard' },
        },
        {
          title: 'Branding',
          icon: 'mdi-palette',
          to: { name: 'BrandingConfig' },
        }
      );
    }

    return items;
  });

  const isItemActive = (item: MenuItem): boolean => {
    return router.resolve(item.to).fullPath === route.fullPath;
  };

  const navigateTo = async (item: MenuItem): Promise<void> => {
    if (!isItemActive(item)) {
      await router.push(item.to);
    }
  };
</script>

<style scoped lang="scss">
  $navbar-height: 70px;

  :deep(.v-navigation-drawer) {
    position: relative;
    height: calc(100vh - $navbar-height);
    margin-top: 0;
    overflow-y: auto;
    flex-shrink: 0;
  }

  :deep(.v-navigation-drawer__content) {
    height: 100%;
  }

  .app-sidebar {
    background-color: var(--background-primary-dark);
    color: white;
  }

  .sidebar-navigation {
    height: 100%;
    display: flex;
    flex-direction: column;
    padding: 0.5rem;
  }

  .sidebar-items {
    flex: 1;
    overflow-y: auto;
  }

  .sidebar-section {
    margin-bottom: 0.25rem;
  }

  .sidebar-item-content {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    min-width: 0;
  }

  .sidebar-section-icon {
    font-size: 0.85rem;
    width: 1.25rem;
    text-align: center;
  }

  .sidebar-section-title {
    font-size: 0.85rem;
    font-weight: 600;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .sidebar-standalone {
    display: flex;
    align-items: center;
    padding: 0.5rem 0.75rem;
    border-radius: 4px;
    cursor: pointer;
    color: var(--lc-bg-white, #fff);
    transition: background-color 0.15s ease;
  }

  .sidebar-standalone:hover {
    background-color: var(--tab-active-bg, var(--color-primary));
  }

  .sidebar-standalone--active {
    background-color: var(--tab-active-bg, var(--color-primary));
    font-weight: 600;
  }

  .sidebar-navigation--collapsed {
    padding: 0.25rem 0;
    align-items: center;
  }

  .sidebar-collapsed-items {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
  }

  .sidebar-collapsed-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 44px;
    cursor: pointer;
    color: var(--lc-bg-white, #fff);
    transition: background-color 0.15s ease;
  }

  .sidebar-collapsed-icon:hover {
    background-color: var(--tab-active-bg, var(--color-primary));
  }

  .sidebar-collapsed-icon--active {
    background-color: var(--tab-active-bg, var(--color-primary));
  }

  .collapsed-icon-i {
    font-size: 1.1rem;
  }

  .sidebar-toggle {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0.625rem;
    cursor: pointer;
    color: var(--lc-bg-white, #fff);
    opacity: 0.6;
    transition:
      opacity 0.15s ease,
      background-color 0.15s ease;
    border-bottom: 1px solid rgba(255, 255, 255, 0.15);
    margin-bottom: 4px;
  }

  .sidebar-toggle:hover {
    opacity: 1;
    background-color: var(--tab-active-bg, var(--color-primary));
    border-radius: 4px;
  }

  :deep(.v-list-item-title) {
    font-size: 0.9rem;
    font-weight: 600;
    border-bottom: 1px solid var(--lc-background-color);
  }

  :deep(.v-list-item):hover {
    background-color: var(--lc-action-hover) !important;
  }

  :deep(.v-list-item) {
    max-height: 35px !important;
    min-height: 35px !important;
  }
</style>
